/**
 * VRG-721 — the error behind an error toast, captured as a small, safe record.
 *
 * Before this, "Report a problem" on an error toast opened the support form and
 * threw the error away: no status, no endpoint, no `ref`, and the type defaulted
 * to Contact support. The form a user reached from a failure knew nothing about
 * the failure. This file is the record that now travels with it — into the form
 * (`client_context.error`), into the quiet automatic report, and onto the
 * internal Support page.
 *
 * ## The shape is a whitelist, and the whitelist is the privacy rule
 *
 *   kind · title · text · status · method · path · route · ref · code · at · page · attribution
 *
 * Never a request body, a response body, a header, a query string or a token.
 * `title`/`text` are the toast's own words, after the provider's sanitising —
 * copy the person has already read on their own screen — and are still passed
 * through email/token redaction because a toast can say "Couldn't invite
 * jane@example.com". The API applies the same whitelist again
 * (`support-error-context.ts`), because its endpoints are reachable without
 * this UI.
 *
 * ## How a toast finds its error
 *
 * Most of the ~160 error-toast call sites pass only `title`/`text`
 * (`getErrorMessage(error)`), not the error. Asking all of them to pass it would
 * hold until the 161st (working-conventions §42). So the API client records every
 * failed request here (`recordApiFailure`, called from `services/index.ts`), and
 * the toast claims the most recent one when it opens:
 *
 *   1. `explicit` — the caller passed `error` to the toast. Always wins.
 *   2. `message`  — a failure from the last few seconds whose server message is
 *                   the toast's text. Strong evidence, so preferred.
 *   3. `recent`   — otherwise the newest failure from the last few seconds.
 *
 * Each failure is claimed at most once, so two toasts cannot both say they were
 * caused by one request. `attribution` records which rule matched; a staffer can
 * weigh a `recent` match accordingly. This is a heuristic and is labelled as one.
 *
 * ZERO imports, deliberately: it is loaded by the API client's guard suite and by
 * its own under node's type-stripping, where any app import drags the store in.
 */

export type ToastErrorKind = 'api' | 'network' | 'client'
export type ToastErrorAttribution = 'explicit' | 'message' | 'recent'

export interface ToastErrorContext {
  kind: ToastErrorKind
  title: string | null
  text: string | null
  status: number | null
  method: string | null
  /** API path, query string removed. */
  path: string | null
  /** `path` with ids collapsed to `:id`. */
  route: string | null
  ref: string | null
  code: string | null
  /** ISO time the toast opened. */
  at: string
  /** Page pathname the toast appeared on, query removed. */
  page: string | null
  attribution: ToastErrorAttribution | null
}

/** What the API client records about a failed request. Facts only, no bodies. */
export interface ApiFailure {
  kind: 'api' | 'network'
  status: number | null
  method: string | null
  path: string | null
  ref: string | null
  code: string | null
  /** The server's `message` — used ONLY to match a toast to its failure, never stored. */
  serverMessage: string | null
  /** `details` was a `{field: string[]}` map: a validation failure the form shows inline. */
  hasFieldErrors: boolean
  at: number
}

const TOKEN_LIKE_RE = /\b[a-zA-Z0-9_-]{15,}\.[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}\b/g
const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g
const UUID_SEGMENT_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const redactText = (text: string | null | undefined): string | null => {
  if (typeof text !== 'string') return null
  const trimmed = text.trim()
  if (!trimmed) return null
  return trimmed.replace(TOKEN_LIKE_RE, '<token>').replace(EMAIL_RE, '<email>').slice(0, 500)
}

/** A URL or path reduced to its pathname. The query string is where emails and search terms live. */
export const pathWithoutQuery = (url: string | null | undefined, base?: string | null): string | null => {
  if (typeof url !== 'string' || !url.trim()) return null
  const isAbsolute = (s: string) => /^[a-z][a-z0-9+.-]*:\/\//i.test(s)
  let path = url.trim()
  // Joined the way AXIOS joins them — `baseURL` + `/` + `url`, not URL resolution,
  // which would drop the `/api` prefix — so the stored path is the one the API
  // logged: `/api/products/admin`.
  if (!isAbsolute(path) && typeof base === 'string' && base.trim()) {
    path = `${base.trim().replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  }
  if (isAbsolute(path)) {
    try {
      path = new URL(path).pathname
    } catch {
      /* malformed — fall through and strip by hand */
    }
  }
  path = path.split('?')[0].split('#')[0]
  if (!path.startsWith('/')) path = `/${path}`
  return (redactText(path) ?? path).slice(0, 300)
}

/**
 * `/api/products/9c1b…/images` → `/api/products/:id/images`.
 * Mirrors `routeTemplate` in the API's `support-error-context.ts`; the server
 * recomputes it rather than trusting this one, since it is half the dedupe key.
 */
export const routeTemplate = (path: string | null): string | null => {
  if (!path) return null
  return path
    .split('/')
    .map((segment) => {
      if (!segment) return segment
      if (UUID_SEGMENT_RE.test(segment)) return ':id'
      if (/^\d+$/.test(segment)) return ':id'
      if (/^[0-9a-f]{8,}$/i.test(segment)) return ':id'
      if (segment.length >= 20 && /\d/.test(segment) && /^[A-Za-z0-9_-]+$/.test(segment)) return ':id'
      if (segment.startsWith('<') && segment.endsWith('>')) return ':id'
      return segment
    })
    .join('/')
}

const isFieldErrorMap = (details: unknown): boolean => {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return false
  const values = Object.values(details as Record<string, unknown>)
  return (
    values.length > 0 &&
    values.every(
      (v) => (Array.isArray(v) && v.every((m) => typeof m === 'string')) || typeof v === 'string',
    )
  )
}

/** Is this an axios-shaped error (has a request config)? */
export const isApiError = (error: unknown): boolean =>
  Boolean(error && typeof error === 'object' && ((error as any).isAxiosError || (error as any).config))

/** Read the facts off an axios error. Returns null for anything that is not one. */
export const apiFailureFromError = (error: unknown, now: number = Date.now()): ApiFailure | null => {
  if (!isApiError(error)) return null
  const e = error as any
  const config = e.config ?? {}
  const response = e.response
  const data = response?.data && typeof response.data === 'object' ? response.data : {}
  const status = typeof response?.status === 'number' ? response.status : null
  const refRaw = typeof e.ref === 'string' ? e.ref : typeof data.ref === 'string' ? data.ref : null
  return {
    kind: response ? 'api' : 'network',
    status,
    method: typeof config.method === 'string' ? config.method.toUpperCase() : null,
    path: pathWithoutQuery(config.url, config.baseURL),
    ref: refRaw && /^[0-9a-f]{6,16}$/i.test(refRaw) ? refRaw.toLowerCase() : null,
    code: typeof data.code === 'string' ? data.code : null,
    serverMessage: typeof data.message === 'string' ? data.message : null,
    hasFieldErrors: isFieldErrorMap(data.details),
    at: now,
  }
}

// ── The recent-failure buffer ─────────────────────────────────────────────────

/** How long after a failure a toast may still claim it. Toasts open in the same tick as `onError`. */
export const RECENT_FAILURE_WINDOW_MS = 3000
const MAX_RECENT_FAILURES = 10

/**
 * Requests whose failure must never be attributed to a toast: the reporting
 * endpoints themselves. A failed automatic report attributed to the next toast
 * would file a report about failing to file a report.
 */
const UNATTRIBUTABLE_PATHS = [/\/support\/error-reports\b/]

let recent: Array<ApiFailure & { claimed: boolean }> = []

export const recordApiFailure = (error: unknown, now: number = Date.now()): ApiFailure | null => {
  try {
    const failure = apiFailureFromError(error, now)
    if (!failure) return null
    if (failure.path && UNATTRIBUTABLE_PATHS.some((re) => re.test(failure.path!))) return failure
    recent = [...recent.filter((f) => now - f.at <= RECENT_FAILURE_WINDOW_MS), { ...failure, claimed: false }].slice(
      -MAX_RECENT_FAILURES,
    )
    return failure
  } catch {
    // Called from the API client's error path. Recording is a nicety; it must
    // never change what the caller receives.
    return null
  }
}

const norm = (s: string | null | undefined) => (s ?? '').trim().replace(/[.!?]+$/, '').toLowerCase()

/**
 * Claim the failure behind a toast that is opening now. See the file header for
 * the three rules. A claimed failure is never handed out again.
 */
export const claimRecentApiFailure = (
  toast: { title?: string | null; text?: string | null },
  now: number = Date.now(),
): { failure: ApiFailure; attribution: ToastErrorAttribution } | null => {
  const live = recent.filter((f) => !f.claimed && now - f.at <= RECENT_FAILURE_WINDOW_MS && f.at <= now)
  if (live.length === 0) return null
  const wanted = [norm(toast.text), norm(toast.title)].filter(Boolean)
  const byMessage = [...live].reverse().find((f) => f.serverMessage && wanted.includes(norm(f.serverMessage)))
  const pick = byMessage ?? live[live.length - 1]
  pick.claimed = true
  const { claimed: _claimed, ...failure } = pick
  return { failure, attribution: byMessage ? 'message' : 'recent' }
}

/** Test seam: forget every recorded failure. */
export const resetRecentApiFailures = () => {
  recent = []
}

// ── Building the record ───────────────────────────────────────────────────────

export const buildToastErrorContext = (params: {
  title?: string | null
  text?: string | null
  failure?: ApiFailure | null
  attribution?: ToastErrorAttribution | null
  page?: string | null
  now?: number
}): ToastErrorContext => {
  const failure = params.failure ?? null
  return {
    kind: failure ? failure.kind : 'client',
    title: redactText(params.title),
    text: redactText(params.text),
    status: failure?.status ?? null,
    method: failure?.method ?? null,
    path: failure?.path ?? null,
    route: routeTemplate(failure?.path ?? null),
    ref: failure?.ref ?? null,
    code: failure?.code ?? null,
    at: new Date(params.now ?? Date.now()).toISOString(),
    page: pathWithoutQuery(params.page ?? null),
    attribution: failure ? params.attribution ?? null : null,
  }
}

/**
 * The client-side dedupe key — the same four facts the API hashes for an HTTP
 * failure (method + route template + status + error code). A pure client error
 * has no request, so it keys on its words and the page instead.
 */
export const errorSignatureKey = (ctx: ToastErrorContext): string =>
  ctx.kind === 'api'
    ? `api ${ctx.method ?? '-'} ${ctx.route ?? '-'} ${ctx.status ?? '-'} ${ctx.code ?? '-'}`
    : `${ctx.kind} ${norm(ctx.text || ctx.title).slice(0, 200)} ${routeTemplate(ctx.page) ?? '-'}`

/**
 * 4xx statuses that are part of a working product, not a bug: the session ran
 * out, a permission is missing, a record is gone, a conflict the copy already
 * explains, a rate limit. A toast for one of these is the app doing its job.
 */
const EXPECTED_CLIENT_STATUSES = new Set([401, 403, 404, 409, 410, 412, 422, 429])

/**
 * Should an error toast be reported automatically, with nobody asking?
 *
 * Yes for:
 *  - a 5xx — the server failed, always worth a look;
 *  - any other 4xx whose server message was NOT a sentence a person could act on
 *    (the provider had to replace it with "Something went wrong"). Nobody wrote
 *    copy for that failure, which is the working definition of unexpected;
 *  - a toast with no HTTP failure behind it whose own text was engineering text
 *    (a leaked `TypeError`), or whose caller passed a real JS `Error`.
 *
 * No for:
 *  - a validation 4xx carrying field errors (the form shows them inline);
 *  - the expected statuses above;
 *  - a network failure with no response (offline, VPN, DNS) — the report would
 *    travel the same broken network, and it is not a defect in the product;
 *  - any app-authored client message ("Couldn't attach x.pdf").
 *
 * The localhost and signed-out gates live in `errorReporter.ts`, with the
 * render-crash path's, so all automatic reports share one notion of "local".
 */
export const shouldAutoReport = (
  ctx: ToastErrorContext,
  facts: { hasFieldErrors?: boolean; messageWasTechnical?: boolean; explicitClientError?: boolean },
): boolean => {
  if (ctx.kind === 'network') return false
  if (ctx.kind === 'api') {
    if (ctx.status === null) return false
    if (ctx.status >= 500) return true
    if (facts.hasFieldErrors) return false
    if (EXPECTED_CLIENT_STATUSES.has(ctx.status)) return false
    return Boolean(facts.messageWasTechnical)
  }
  return Boolean(facts.explicitClientError || facts.messageWasTechnical)
}
